import { useState, useEffect } from 'react';
import { Calendar, AlertCircle, CheckCircle, X } from 'lucide-react';
import { 
  getRoomReservations, 
  checkRoomAvailability, 
  createReservation,
  type Reservation 
} from '../../services/reservationService';

interface EnhancedCheckInModalProps {
  room: any;
  onClose: () => void;
  onCheckIn: (roomNumber: string, guestData: any) => void;
}

export function EnhancedCheckInModal({ room, onClose, onCheckIn }: EnhancedCheckInModalProps) {
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [availabilityCheck, setAvailabilityCheck] = useState<{available: boolean, message: string} | null>(null);
  const [bookingType, setBookingType] = useState<'immediate' | 'reservation'>('immediate');
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    check_in: new Date().toISOString().split('T')[0],
    check_out: new Date(Date.now() + 86400000).toISOString().split('T')[0],
    id_proof: '',
    special_requests: '',
    custom_price: 0
  });

  // Load existing reservations
  useEffect(() => {
    loadReservations();
  }, [room.room_number]);

  // Check availability when dates change
  useEffect(() => {
    if (formData.check_in && formData.check_out) {
      checkAvailability();
    }
  }, [formData.check_in, formData.check_out]);

  const loadReservations = async () => {
    try {
      const roomReservations = await getRoomReservations(room.room_number);
      setReservations(roomReservations);
    } catch (error) {
      console.error('Error loading reservations:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkAvailability = async () => {
    try {
      const isAvailable = await checkRoomAvailability(
        room.room_number,
        formData.check_in,
        formData.check_out
      );

      if (isAvailable) {
        setAvailabilityCheck({
          available: true,
          message: '✓ Room is available for selected dates'
        });
      } else {
        setAvailabilityCheck({
          available: false,
          message: '✗ Room is already booked for these dates'
        });
      }
    } catch (error) {
      console.error('Error checking availability:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!availabilityCheck?.available) {
      alert('Room is not available for selected dates!');
      return;
    }

    try {
      if (bookingType === 'reservation') {
        // Create future reservation
        await createReservation({
          roomNumber: room.room_number,
          guestName: formData.name,
          guestPhone: formData.phone,
          guestEmail: formData.email,
          checkIn: formData.check_in,
          checkOut: formData.check_out,
          guests: 2,
          idProofNumber: formData.id_proof,
          specialRequests: formData.special_requests,
          customPrice: formData.custom_price,
          status: 'confirmed',
          paymentStatus: 'pending'
        });
        alert('✅ Reservation created successfully!');
      } else {
        // Immediate check-in
        onCheckIn(room.room_number, formData);
      }
      onClose();
    } catch (error: any) {
      alert('❌ Error: ' + error.message);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'bg-blue-500/20 text-blue-400 border-blue-400/30';
      case 'checked-in': return 'bg-green-500/20 text-green-400 border-green-400/30';
      case 'checked-out': return 'bg-gray-500/20 text-gray-400 border-gray-400/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-400/30';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="glass-luxury rounded-3xl shadow-luxury-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gold-400/20">
        {/* Header */}
        <div className="p-6 border-b border-gold-400/20 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-gold-400 mb-2">
              Room {room.room_number} - {room.type}
            </h3>
            <p className="text-cream-300">Check-in & Reservation Management</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-cream-300" />
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-6 p-6">
          {/* Left: Existing Reservations */}
          <div>
            <h4 className="text-lg font-bold text-gold-400 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Existing Reservations
            </h4>

            {loading ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-2 border-gold-400 border-t-transparent rounded-full animate-spin mx-auto"></div>
              </div>
            ) : reservations.length > 0 ? (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {reservations.map((res) => (
                  <div
                    key={res.id}
                    className={`p-4 rounded-xl border ${getStatusColor(res.status)}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-bold">{res.guestName}</p>
                        <p className="text-sm opacity-80">{res.guestPhone}</p>
                      </div>
                      <span className="text-xs px-2 py-1 rounded-full bg-white/10">
                        {res.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(res.checkIn)} → {formatDate(res.checkOut)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-white/5 rounded-xl border border-gold-400/10">
                <Calendar className="w-12 h-12 text-cream-400 mx-auto mb-3 opacity-50" />
                <p className="text-cream-400">No existing reservations</p>
                <p className="text-sm text-cream-500 mt-1">Room is completely available</p>
              </div>
            )}

            {/* Available Date Ranges Info */}
            <div className="mt-4 p-4 bg-green-500/10 rounded-xl border border-green-400/20">
              <p className="text-sm text-green-300 font-medium mb-2">💡 Booking Tip:</p>
              <p className="text-xs text-green-400">
                You can book this room for any dates that don't overlap with existing reservations.
                The system will automatically check for conflicts.
              </p>
            </div>
          </div>

          {/* Right: Booking Form */}
          <div>
            <h4 className="text-lg font-bold text-gold-400 mb-4">New Booking</h4>

            {/* Booking Type Toggle */}
            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => setBookingType('immediate')}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                  bookingType === 'immediate'
                    ? 'bg-gold-gradient text-navy-900'
                    : 'bg-white/10 text-cream-300 hover:bg-white/20'
                }`}
              >
                Check-in Now
              </button>
              <button
                type="button"
                onClick={() => setBookingType('reservation')}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                  bookingType === 'reservation'
                    ? 'bg-gold-gradient text-navy-900'
                    : 'bg-white/10 text-cream-300 hover:bg-white/20'
                }`}
              >
                Future Reservation
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gold-300 mb-2">Guest Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 border border-gold-400/30 rounded-xl focus:ring-2 focus:ring-gold-500 text-white placeholder-cream-300"
                  placeholder="Enter guest name"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gold-300 mb-2">Phone *</label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 border border-gold-400/30 rounded-xl focus:ring-2 focus:ring-gold-500 text-white placeholder-cream-300"
                    placeholder="+91 XXXXX"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gold-300 mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-white/10 border border-gold-400/30 rounded-xl focus:ring-2 focus:ring-gold-500 text-white placeholder-cream-300"
                    placeholder="email@example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gold-300 mb-2">Check-in *</label>
                  <input
                    type="date"
                    required
                    value={formData.check_in}
                    onChange={(e) => setFormData({ ...formData, check_in: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 bg-white/10 border border-gold-400/30 rounded-xl focus:ring-2 focus:ring-gold-500 text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gold-300 mb-2">Check-out *</label>
                  <input
                    type="date"
                    required
                    value={formData.check_out}
                    onChange={(e) => setFormData({ ...formData, check_out: e.target.value })}
                    min={formData.check_in}
                    className="w-full px-4 py-3 bg-white/10 border border-gold-400/30 rounded-xl focus:ring-2 focus:ring-gold-500 text-white"
                  />
                </div>
              </div>

              {/* Availability Status */}
              {availabilityCheck && (
                <div className={`p-3 rounded-xl border flex items-center gap-2 ${
                  availabilityCheck.available
                    ? 'bg-green-500/10 border-green-400/30 text-green-400'
                    : 'bg-red-500/10 border-red-400/30 text-red-400'
                }`}>
                  {availabilityCheck.available ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <AlertCircle className="w-5 h-5" />
                  )}
                  <span className="text-sm font-medium">{availabilityCheck.message}</span>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gold-300 mb-2">ID Proof</label>
                <input
                  type="text"
                  value={formData.id_proof}
                  onChange={(e) => setFormData({ ...formData, id_proof: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 border border-gold-400/30 rounded-xl focus:ring-2 focus:ring-gold-500 text-white placeholder-cream-300"
                  placeholder="Aadhar/License/PAN"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gold-300 mb-2">
                  Custom Price (Optional)
                  <span className="text-xs text-cream-400 ml-2">₹{room.price}/night default</span>
                </label>
                <input
                  type="number"
                  value={formData.custom_price}
                  onChange={(e) => setFormData({ ...formData, custom_price: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-3 bg-white/10 border border-gold-400/30 rounded-xl focus:ring-2 focus:ring-gold-500 text-white placeholder-cream-300"
                  placeholder="0"
                  min="0"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-6 py-3 border border-gold-400/30 text-cream-300 rounded-xl hover:bg-white/5 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!availabilityCheck?.available}
                  className="flex-1 px-6 py-3 bg-gold-gradient text-navy-900 rounded-xl hover:shadow-gold transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {bookingType === 'immediate' ? 'Check In Guest' : 'Create Reservation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
